/**
 * auth/AuthContext.tsx — the SPA's single source of truth for
 * authentication state.
 *
 * State:
 *   * `status`         — 'loading' | 'signed_out' | 'signed_in'
 *   * `session`        — { user_id, session_kind, absolute_expires_at } | null
 *   * `signedOutFor`   — 'idle' | 'absolute' | null — the reason the
 *                        session dropped. When non-null, /signin
 *                        shows "Signed out for security" instead of
 *                        the neutral welcome copy.
 *   * `signIn(dev)`    — dev-only phone/challenge/verify flow that
 *                        ends by exchanging the bearer for cookies.
 *   * `signOut()`      — revokes the web session server-side and
 *                        clears local state.
 *   * `refresh()`      — re-probes /v1/auth/web/session.
 *
 * CSRF token is NOT stored here — the API client reads it directly
 * from the `aster_csrf` cookie per-request (see api/client.ts).
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import {
  exchangeBearerForCookie,
  getCurrentWebSession,
  logoutWebSession,
  type WebSessionOut,
} from "../api/session";
import { api, onSessionExpired } from "../api/client";

// ---------------------------------------------------------------------------
// Reducer state.
// ---------------------------------------------------------------------------

type Status = "loading" | "signed_out" | "signed_in";
type SignedOutReason = "idle" | "absolute" | null;

interface AuthState {
  status: Status;
  session: WebSessionOut | null;
  error: string | null;
  signedOutFor: SignedOutReason;
}

type Action =
  | { type: "boot_start" }
  | { type: "boot_done"; session: WebSessionOut | null }
  | { type: "sign_in_start" }
  | { type: "sign_in_success"; session: WebSessionOut }
  | { type: "sign_in_error"; message: string }
  | { type: "sign_out" }
  | { type: "session_expired"; reason: "idle" | "absolute" }
  | { type: "clear_error" }
  | { type: "clear_signed_out_reason" };

const initial: AuthState = {
  status: "loading",
  session: null,
  error: null,
  signedOutFor: null,
};

function reducer(state: AuthState, a: Action): AuthState {
  switch (a.type) {
    case "boot_start":
      return { ...state, status: "loading", error: null };
    case "boot_done":
      return a.session
        ? { status: "signed_in", session: a.session, error: null, signedOutFor: null }
        : { status: "signed_out", session: null, error: null, signedOutFor: state.signedOutFor };
    case "sign_in_start":
      return { ...state, status: "loading", error: null, signedOutFor: null };
    case "sign_in_success":
      return { status: "signed_in", session: a.session, error: null, signedOutFor: null };
    case "sign_in_error":
      return { status: "signed_out", session: null, error: a.message, signedOutFor: null };
    case "sign_out":
      return { status: "signed_out", session: null, error: null, signedOutFor: null };
    case "session_expired":
      return { status: "signed_out", session: null, error: null, signedOutFor: a.reason };
    case "clear_error":
      return { ...state, error: null };
    case "clear_signed_out_reason":
      // Explicitly drop the "Signed out for security" banner. Called
      // when the user starts typing on /signin — the banner is context
      // for the arrival, not persistent state that shadows the new
      // attempt.
      return { ...state, signedOutFor: null };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Public API surface.
// ---------------------------------------------------------------------------

interface AuthContextValue extends AuthState {
  signInDev(phone?: string): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
  /** Explicitly clear the "Signed out for security" banner (e.g. when
   * the user starts typing their credentials again). */
  clearSignedOutReason(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider.
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initial);

  // ---- Boot: try to resurrect any existing cookie ---------------------
  useEffect(() => {
    let cancelled = false;
    dispatch({ type: "boot_start" });
    getCurrentWebSession()
      .then((session) => {
        if (!cancelled) dispatch({ type: "boot_done", session });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "boot_done", session: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Global session-expiry subscription ------------------------------
  // Any api() call that receives `web_session_idle_expired` or
  // `web_session_absolute_expired` fans out here so we drop the local
  // session, and the ProtectedRoute redirects to /signin — which
  // reads `signedOutFor` to show the correct message.
  useEffect(() => {
    return onSessionExpired((code) => {
      const reason: "idle" | "absolute" =
        code === "web_session_idle_expired" ? "idle" : "absolute";
      dispatch({ type: "session_expired", reason });
    });
  }, []);

  // ---- Dev sign-in ----------------------------------------------------
  //
  // The dev-only sign-in path. Uses `DEV-SIG:{challenge}` which is
  // accepted by the backend ONLY when `AUTH_ALLOW_DEV_SIGNATURE` is
  // permissive AND `ENVIRONMENT != production` (see
  // backend/auth/security.py). In production the backend rejects it.
  //
  // The `import.meta.env.DEV` guard causes Vite's terser to eliminate
  // the entire dev body from production builds — the compiled bundle
  // contains only the `throw` branch. That keeps the `DEV-SIG:` string
  // and the challenge/verify wiring out of the production JS entirely.
  const signInDev = useCallback(async (_phone?: string) => {
    if (!import.meta.env.DEV) {
      // Prod bundle path — terser eliminates everything below.
      throw new Error(
        "signInDev is not available in production. Sign in from the " +
          "AsterWallet mobile app and hand a session off to the web.",
      );
    }
    dispatch({ type: "sign_in_start" });
    const deviceId = `web-dev-${
      crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)
    }`;
    const publicKey = `web-dev-pk-${
      crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)
    }`;
    try {
      const challengeResp = await api<{ challenge: string; expires_at: string }>(
        "/v1/auth/challenge",
        {
          method: "POST",
          body: { purpose: "sign_in" },
          deviceId,
        },
      );
      const tokens = await api<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
      }>("/v1/auth/verify", {
        method: "POST",
        body: {
          challenge: challengeResp.challenge,
          signature: `DEV-SIG:${challengeResp.challenge}`,
          public_key: publicKey,
        },
        deviceId,
      });
      const session = await exchangeBearerForCookie(
        tokens.access_token,
        deviceId,
      );
      dispatch({ type: "sign_in_success", session });
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Sign-in failed. Please try again.";
      dispatch({ type: "sign_in_error", message });
      throw e;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutWebSession();
    } finally {
      dispatch({ type: "sign_out" });
    }
  }, []);

  const refresh = useCallback(async () => {
    const session = await getCurrentWebSession();
    dispatch({ type: "boot_done", session });
  }, []);

  const clearSignedOutReason = useCallback(() => {
    dispatch({ type: "clear_signed_out_reason" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signInDev, signOut, refresh, clearSignedOutReason }),
    [state, signInDev, signOut, refresh, clearSignedOutReason],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook.
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error(
      "useAuth() called outside <AuthProvider>. Wrap your app once at the root.",
    );
  }
  return ctx;
}
